/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import { useQuery } from "@tanstack/react-query"
import { Card, Divider } from "antd"
import type React from "react"
import { useEffect, useState } from "react"
import UserSearchForm from "./components/UserSearchForm"
import UserTable from "./components/UserTable"
import UserTableActions from "./components/UserTableActions"
import UserLogsModal from "./components/UserLogsModal"
import UserSrsLogsModal from "./components/UserSrsLogsModal"
import UserSetsModal from "./components/UserSetsModal"
import { UserEditModal } from "./components/UserEditModal"
import { UserTempPasswordModal } from "./components/UserTempPasswordModal"
import { userService, type UserSearchParams, type UserApiParams } from "@/services/user/userApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const UserPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<UserSearchParams>({
    ...PAGE_CONFIG,
    search: "",
    is_test: undefined,
    is_online: undefined,
  })
  const [total, setTotal] = useState<number>(0)

  // Modal states
  const [logsModalUser, setLogsModalUser] = useState<any | null>(null)
  const [srsLogsModalUser, setSrsLogsModalUser] = useState<any | null>(null)
  const [setsModalUser, setSetsModalUser] = useState<any | null>(null)
  const [editModalUser, setEditModalUser] = useState<any | null>(null)
  const [tempPasswordModalUser, setTempPasswordModalUser] = useState<any | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["user_list", searchParams],
    queryFn: () => {
      const apiParams: UserApiParams = {
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        is_test: searchParams.is_test,
        is_online: searchParams.is_online,
      }
      return userService.list(apiParams)
    },
  })

  useEffect(() => {
    if (result?.paginate.total) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const handleSearch = (values: Omit<UserSearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const tableLoading = isFetching

  return (
    <div className="flex flex-col gap-2">
      <style>{`
        .text-primary {
          color: var(--ant-color-primary, #1677ff) !important;
        }
        .border-primary {
          border-color: var(--ant-color-primary, #1677ff) !important;
        }
      `}</style>
      <UserSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          is_test: searchParams.is_test,
          is_online: searchParams.is_online,
        }}
      />
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Danh sách người dùng (User)</h2>
            <Divider orientation="vertical" />
            <span className="text-gray-500 text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</span>
            <Divider orientation="vertical" />
            <UserTableActions onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <UserTable
          data={result?.data || []}
          loading={tableLoading}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          onOpenLogs={(user) => setLogsModalUser(user)}
          onOpenSrsLogs={(user) => setSrsLogsModalUser(user)}
          onOpenSets={(user) => setSetsModalUser(user)}
          onEdit={(user) => setEditModalUser(user)}
          onOpenTempPassword={(user) => setTempPasswordModalUser(user)}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      {/* User Edit Modal */}
      <UserEditModal
        open={!!editModalUser}
        user={editModalUser}
        onClose={() => setEditModalUser(null)}
        onSuccess={() => refetch()}
      />

      {/* User Logs Modal (Search logs + Add vocab logs) */}
      <UserLogsModal open={!!logsModalUser} user={logsModalUser} onClose={() => setLogsModalUser(null)} />

      {/* User SRS Learning Logs Modal */}
      <UserSrsLogsModal open={!!srsLogsModalUser} user={srsLogsModalUser} onClose={() => setSrsLogsModalUser(null)} />

      {/* User Sets Modal (User created sets + Learned Jupi sets) */}
      <UserSetsModal open={!!setsModalUser} user={setsModalUser} onClose={() => setSetsModalUser(null)} />

      {/* User Impersonation / Temp Password Modal */}
      <UserTempPasswordModal
        open={!!tempPasswordModalUser}
        user={tempPasswordModalUser}
        onClose={() => setTempPasswordModalUser(null)}
      />
    </div>
  )
}

export default UserPage
